/**
 * Stormforge Mode Visual Overlay
 * Phase 8: Lightning effects and visual enhancements
 */

import { useEffect, useState } from 'react';
import { useMarketingMode } from '@/contexts/MarketingModeContext';

interface LightningBolt {
  id: number;
  x: number;
  delay: number;
  duration: number;
}

export function StormforgeOverlay() {
  const { isStormforgeMode } = useMarketingMode();
  const [bolts, setBolts] = useState<LightningBolt[]>([]);
  const [flashOpacity, setFlashOpacity] = useState(0);

  // Generate random lightning bolts
  useEffect(() => {
    if (!isStormforgeMode) {
      setBolts([]);
      return;
    }

    const generateBolt = (): LightningBolt => ({
      id: Math.random(),
      x: Math.random() * 100,
      delay: Math.random() * 2,
      duration: 0.15 + Math.random() * 0.1,
    });

    // Initial bolts
    setBolts([generateBolt(), generateBolt()]);

    // Periodically add new bolts
    const interval = setInterval(() => {
      setBolts((prev) => {
        const newBolts = [...prev.slice(-3), generateBolt()];
        return newBolts;
      });
      
      // Random flash effect
      if (Math.random() > 0.6) {
        setFlashOpacity(0.15);
        setTimeout(() => setFlashOpacity(0), 100);
      }
    }, 2000 + Math.random() * 3000);

    return () => clearInterval(interval);
  }, [isStormforgeMode]);

  if (!isStormforgeMode) return null;

  return (
    <>
      {/* Global dark overlay with purple tint */}
      <div 
        className="fixed inset-0 pointer-events-none z-[9998] mix-blend-multiply transition-opacity duration-500"
        style={{
          background: 'linear-gradient(180deg, rgba(88, 28, 135, 0.15) 0%, rgba(15, 23, 42, 0.1) 100%)',
        }}
      />

      {/* Flash effect */}
      <div 
        className="fixed inset-0 pointer-events-none z-[9999] bg-purple-300 transition-opacity duration-75"
        style={{ opacity: flashOpacity }}
      />

      {/* Lightning bolts */}
      <svg
        className="fixed inset-0 w-full h-full pointer-events-none z-[9997]"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <filter id="stormforge-glow">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        
        {bolts.map((bolt) => (
          <g
            key={bolt.id}
            style={{
              animation: `stormforge-bolt ${bolt.duration}s ease-out ${bolt.delay}s infinite`,
              transformOrigin: 'top center',
            }}
          >
            <path
              d={generateLightningPath(bolt.x)}
              stroke="url(#stormforge-gradient)"
              strokeWidth="2"
              fill="none"
              filter="url(#stormforge-glow)"
              opacity="0.8"
            />
          </g>
        ))}
        
        <defs>
          <linearGradient id="stormforge-gradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#c4b5fd" />
            <stop offset="50%" stopColor="#a78bfa" />
            <stop offset="100%" stopColor="#7c3aed" />
          </linearGradient>
        </defs>
      </svg>

      {/* Ambient particles */}
      <div className="fixed inset-0 pointer-events-none z-[9996] overflow-hidden">
        {[...Array(12)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 rounded-full bg-purple-400/60"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animation: `stormforge-particle ${3 + Math.random() * 4}s ease-in-out ${Math.random() * 2}s infinite`,
            }}
          />
        ))}
      </div>

      {/* Stormforge badge. OCT20D-F1: it sat at bottom-4 right-4, z-10000 — on top of the tutorial Help button (1440 px²
          overlap; Help's lower half went to the badge). It is decorative, so it ignores the pointer like the rest of
          this overlay, sits bottom-centre on the floating rail, and is a registered floating control: the page keeps
          its band clear and Report an Issue steps around it. */}
      <div data-floating-control="stormforge-badge"
        className="pointer-events-none fixed bottom-4 left-1/2 -translate-x-1/2 mb-safe-bottom z-[10000] flex items-center gap-2 px-3 py-1.5 rounded-full bg-purple-900/80 border border-purple-500/50 backdrop-blur-sm shadow-lg shadow-purple-500/20">
        <span className="text-purple-300 text-xs font-bold tracking-wider animate-pulse">
          ⚡ STORMFORGE
        </span>
      </div>

      {/* Inject keyframe animations */}
      <style>{`
        @keyframes stormforge-bolt {
          0%, 100% { opacity: 0; transform: scaleY(0); }
          10%, 30% { opacity: 1; transform: scaleY(1); }
          50% { opacity: 0.3; }
        }
        
        @keyframes stormforge-particle {
          0%, 100% { 
            opacity: 0; 
            transform: translateY(0) scale(1); 
          }
          50% { 
            opacity: 0.8; 
            transform: translateY(-20px) scale(1.5); 
          }
        }
      `}</style>
    </>
  );
}

// Generate a jagged lightning path
function generateLightningPath(startX: number): string {
  const segments = 6 + Math.floor(Math.random() * 4);
  const height = window.innerHeight;
  const segmentHeight = height / segments;
  
  let path = `M ${(startX / 100) * window.innerWidth} 0`;
  let currentX = (startX / 100) * window.innerWidth;
  
  for (let i = 1; i <= segments; i++) {
    const jitter = (Math.random() - 0.5) * 80;
    currentX += jitter;
    const y = i * segmentHeight;
    path += ` L ${currentX} ${y}`;
  }
  
  return path;
}
